/**
 * Условие ruleCondition для групп трафика с числовыми идентификаторами
 * @param {}
 * @return {}
 */

import React from "react";
import type { ReactNode } from "react";
import { Col, Form, Row, Select } from "antd";
import { useAppSelector } from "../../../../../..";
import type { IRuleCondition } from "../../../../../../lib/types/IRuleCondition";
import { ReactComponent as DeleteIcon } from "../../../../../../img/delete-round.svg";
import { ReactComponent as PencilPlusIcon } from "../../../../../../img/pencil-plus-currentColor.svg";
import MultipleSelect from "../../../../../../components/common/MultipleSelect";
import { checkIsLoading } from "../../../../../../lib/utils";
import { IOfferListSelectMode } from "../../../../../../containers/OfferContainer/context/types";
import "./RuleConditionEdit.less";

type FieldName = "includeList" | "documentTypeList" | "direction";

const fieldNames: Record<FieldName, string> = {
  includeList: "Контрагент",
  documentTypeList: "Тип",
  direction: "Направление",
};

const columnSpans: Record<FieldName | "btns", number> = {
  btns: 2,
  includeList: 8,
  documentTypeList: 6,
  direction: 8,
};

interface ComponentProps {
  aggRuleIdx: number;
  newRuleCondition: IRuleCondition;
  requiredFields?: FieldName[];
  availableFields: FieldName[];
  getIncludeListTitle: (aggRuleIdx: number, idx: number) => string;
  getDocumentTypeListTitle: (aggRuleIdx: number, idx: number) => string;
  offerListSelectMode: IOfferListSelectMode | undefined;
}

export const RuleConditionEdit = (props: ComponentProps) => {
  const {
    aggRuleIdx,
    newRuleCondition,
    requiredFields = [],
    availableFields,
    getIncludeListTitle,
    getDocumentTypeListTitle,
    offerListSelectMode,
  } = props;

  const {
    retailNetworks: { retailNetworksDic },
    retailNetworksLoading,
    serviceUsageDocumentType,
    serviceUsageDocumentTypeLoading,
    serviceUsageDirection,
    serviceUsageDirectionLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const getHeader = (field: FieldName): ReactNode => (
    <>
      <label className="tariff-aggregationRule-edit__fieldLabel">{fieldNames[field]}</label>
      {requiredFields.includes(field) && <span style={{ color: "red" }}>&nbsp;&nbsp;*</span>}
    </>
  );

  const getFieldProps = (field: FieldName) => ({
    className: "form-item-required__hidden-error__mb0",
    rules: [{ required: requiredFields.includes(field) }],
  });

  const commonDisable = offerListSelectMode === "select";

  return (
    <Form.List name={[aggRuleIdx, "ruleCondition"]}>
      {(fields, { add, remove }) => {
        const onAdd = () => add(newRuleCondition);
        const onRemove = (name: number) => remove(name);
        return (
          <>
            {fields.map(({ key, ...field }, idx) => (
              <Row
                gutter={20}
                style={
                  idx > 0
                    ? {
                        marginTop: "10px",
                      }
                    : {}
                }
                key={key}
              >
                {!commonDisable && (
                  <Col span={columnSpans.btns}>
                    {aggRuleIdx === 0 && idx === 0 && <div style={{ height: "20px" }} />}
                    {availableFields.length > 0 && (
                      <div>
                        {idx === 0 && (
                          <PencilPlusIcon
                            onClick={onAdd}
                            className="rule-condition-edit__addButton"
                            color="#00AAFF"
                          />
                        )}
                        {idx !== 0 && (
                          <DeleteIcon
                            className="rule-condition-edit__deleteButton"
                            onClick={() => onRemove(idx)}
                          />
                        )}
                      </div>
                    )}
                  </Col>
                )}
                <Col span={columnSpans.includeList}>
                  {aggRuleIdx === 0 && idx === 0 && getHeader("includeList")}
                  <Form.Item
                    {...field}
                    name={[idx, "includeList"]}
                    {...getFieldProps("includeList")}
                  >
                    <MultipleSelect
                      showArrow
                      filterOption={(inputValue, option) =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      allValues={retailNetworksDic.map(({ objectId }) => objectId)}
                      style={{ width: "100%" }}
                      loading={checkIsLoading(retailNetworksLoading)}
                      maxTagCount={1}
                      disabled={!availableFields.includes("includeList") || commonDisable}
                      title={getIncludeListTitle(aggRuleIdx, idx)}
                    >
                      {retailNetworksDic.map(({ objectId, stringValue }) => (
                        <Select.Option value={objectId} key={objectId} name={stringValue}>
                          {stringValue}
                        </Select.Option>
                      ))}
                    </MultipleSelect>
                  </Form.Item>
                </Col>
                <Col span={columnSpans.documentTypeList}>
                  {aggRuleIdx === 0 && idx === 0 && getHeader("documentTypeList")}
                  <Form.Item
                    {...field}
                    name={[idx, "documentTypeList"]}
                    {...getFieldProps("documentTypeList")}
                  >
                    <MultipleSelect
                      showArrow
                      allValues={serviceUsageDocumentType.map(({ id }) => id)}
                      style={{ width: "100%" }}
                      filterOption={(inputValue, option) =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      maxTagCount={1}
                      title={getDocumentTypeListTitle(aggRuleIdx, idx)}
                      disabled={!availableFields.includes("documentTypeList") || commonDisable}
                      loading={checkIsLoading(serviceUsageDocumentTypeLoading)}
                    >
                      {serviceUsageDocumentType.map(({ id, name, alias }) => (
                        <Select.Option value={id} key={id} name={alias}>
                          {name}
                        </Select.Option>
                      ))}
                    </MultipleSelect>
                  </Form.Item>
                </Col>
                <Col span={columnSpans.direction}>
                  {aggRuleIdx === 0 && idx === 0 && getHeader("direction")}
                  <Form.Item {...field} name={[idx, "direction"]} {...getFieldProps("direction")}>
                    <Select
                      showArrow
                      style={{ width: "100%" }}
                      disabled={!availableFields.includes("direction") || commonDisable}
                      loading={checkIsLoading(serviceUsageDirectionLoading)}
                    >
                      {serviceUsageDirection.map(({ id, name, alias }) => (
                        <Select.Option value={alias} key={id}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </>
        );
      }}
    </Form.List>
  );
};
